import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import DetailedResourceView from './DetailedResourceView';

const ResourceCard = ({ resource, featured = false }) => {
  const [showDetailedView, setShowDetailedView] = useState(false);
  const getTypeIcon = (type) => {
    switch (type) {
      case 'guide': return 'BookOpen';
      case 'report': return 'FileText';
      case 'webinar': return 'Video';
      case 'whitepaper': return 'FileDown';
      case 'case-study': return 'Target';
      case 'checklist': return 'CheckSquare';
      default: return 'File';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'guide': return 'text-primary bg-primary/10';
      case 'report': return 'text-success bg-success/10';
      case 'webinar': return 'text-accent bg-accent/10';
      case 'whitepaper': return 'text-warning bg-warning/10';
      case 'case-study': return 'text-error bg-error/10';
      case 'checklist': return 'text-secondary bg-secondary/10';
      default: return 'text-text-secondary bg-muted';
    }
  };

  return (
    <Link to={`/resources-insights-knowledge-authority/insight/${resource.id}`} className={`group card-interactive rounded-xl overflow-hidden block ${
      featured ? 'lg:col-span-2' : ''
    }`}>
      {resource.image && (
        <div className="relative h-48 overflow-hidden">
          <Image
            src={resource.image}
            alt={resource.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-4 left-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
              <Icon name={getTypeIcon(resource.type)} size={14} className="mr-1" />
              {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
            </span>
          </div>
          {resource.featured && (
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                <Icon name="Star" size={12} className="mr-1" />
                Featured
              </span>
            </div>
          )}
        </div>
      )}
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className={`font-headline font-semibold text-text-primary group-hover:text-primary transition-colors duration-300 ${
              featured ? 'text-xl' : 'text-lg'
            }`}>
              {resource.title}
            </h3>
            <p className="text-text-secondary text-sm mt-2 line-clamp-2">
              {resource.description}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 text-sm text-text-secondary">
            <div className="flex items-center">
              <Icon name="Clock" size={14} className="mr-1" />
              {resource.readTime}
            </div>
            <div className="flex items-center">
              <Icon name="Calendar" size={14} className="mr-1" />
              {resource.publishDate}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Image
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face"
              alt="Sanjay Jain"
              className="w-8 h-8 rounded-full"
            />
            <div>
              <p className="text-sm font-medium text-text-primary">Sanjay Jain</p>
              <p className="text-xs text-text-secondary">Chief Strategy Officer & GCC Expert</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {resource.relevantFor.map((role, index) => (
              <span
                key={index}
                className="inline-block px-2 py-1 text-xs bg-muted text-text-secondary rounded"
              >
                {role}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={14}
                  className={i < resource.rating ? 'text-warning fill-current' : 'text-muted'}
                />
              ))}
              <span className="text-sm text-text-secondary ml-1">({resource.reviews})</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors duration-300">
              <Icon name="Bookmark" size={16} className="text-text-secondary hover:text-primary" />
            </button>
            <button className="p-2 rounded-lg hover:bg-muted transition-colors duration-300">
              <Icon name="Share2" size={16} className="text-text-secondary hover:text-primary" />
            </button>
            <button
              onClick={() => setShowDetailedView(true)}
              className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-300 text-sm font-medium"
            >
              <Icon name="Eye" size={14} className="mr-2" />
              View
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ResourceCard;